import { useEffect, useState } from "react";
import {
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    Stack,
    Modal,
    Fade,
    Backdrop,
} from "@mui/material";
import Layout from "../../../components/layouts/Layout";
import { Space } from "../../../types/spaceTypes";
import AddSpace from "./AddSpace";
import EditSpace from "./EditSpace";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import { toast } from "react-toastify";
import { getSpaces, removeSpace } from "../../../service/spaceService";
import { useNavigate } from "react-router-dom";

const SpaceList = () => {
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [open, setOpen] = useState<boolean>(false);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [spaceToDelete, setSpaceToDelete] = useState<number | null>(null);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [editSpace, setEditSpace] = useState<Space | null>(null);
    const [filterType, setFilterType] = useState<string>('');

    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    const userRole = userData?.role;

    const navigate = useNavigate();

    const fetchSpaces = async (type?: string) => {
        setLoading(true);
        try {
            const data = await getSpaces(type); // pass type to service
            setSpaces(data);
        } catch (error) {
            console.error("Failed to fetch spaces");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchSpaces(filterType);
    }, [filterType]);

    const confirmDelete = (spaceId: number) => {
        setSpaceToDelete(spaceId);
        setConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!spaceToDelete) return;
        try {
            await removeSpace(spaceToDelete);
            toast.success("Space deleted successfully!");
            fetchSpaces(); // Refresh space list
        } catch (error) {
            toast.error("Failed to delete space.");
            console.error("Error deleting space:", error);
        } finally {
            setConfirmOpen(false);
            setSpaceToDelete(null);
        }
    };

    const handleEditSpace = async (space: Space) => {
        setEditSpace(space);
        setOpenEditModal(true);
    };

    const handleViewRepo = (spaceId: number) => {
        if (userRole == 'admin') {
            navigate(`/admin/folderRepo/${spaceId}`);
        }

        if (userRole == 'superadmin') {
            navigate(`/sa/folderRepo/${spaceId}`);
        }
    };

    return (
        <Layout>
            {/* Title & Add New Space Button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                    Spaces
                </Typography>
                <Button variant="contained" color="primary" sx={{ color: "white" }} onClick={() => setOpen(true)}>
                    Add New Space (Admin)
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {userData.role == 'superadmin' && (
                        <Box display="flex" mb={2}>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                style={{ padding: '8px', borderRadius: 4, minWidth: 180 }}
                            >
                                <option value="">- View All -</option>
                                <option value="admin">By Admin</option>
                                <option value="company">By Company</option>
                            </select>
                        </Box>
                    )}


                    {spaces.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">
                            <p>No {filterType} space.</p>
                        </div>
                    ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none" }}>
                            <Table sx={{ background: "white" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Space Name</TableCell>
                                        <TableCell>Space Type</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {spaces.map((space, index) => {
                                        return (
                                            <TableRow key={space.id}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{space.space_name}</TableCell>
                                                <TableCell>{space.space_type}</TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#17a2b8", color: "white" }}
                                                            onClick={() => handleViewRepo(space.id)}
                                                        >
                                                            View Space
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#6c757d", color: "white" }}
                                                            onClick={() => handleEditSpace(space)}>Edit</Button>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#dc3545", color: "white" }}
                                                            onClick={() => confirmDelete(space.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )
            }

            {/* Add Space Modal */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <AddSpace onClose={() => { setOpen(false); fetchSpaces(); }} />
                    </Box>
                </Fade>
            </Modal>


            {/* Edit Space Modal */}
            <EditSpace open={openEditModal} onClose={() => setOpenEditModal(false)} space={editSpace} refreshSpaces={fetchSpaces} />

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Delete Space"
                message="Are you sure you want to delete this space? This action cannot be undone."
            />
        </Layout >
    );
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 620,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 3,
};

export default SpaceList;
